import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import css from './InstructionsToUse.module.css';
import ReactPlayer from 'react-player';
import { isHTMLString, sanitizeHtml } from '../../../util/data';

const getVisibleText = str => {
  if (!str) return '';
  if (isHTMLString(str) && typeof window !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = str;
    return tempDiv.textContent || tempDiv.innerText || '';
  }
  return str;
};

const truncateHTML = (htmlString, maxLength = 1000) => {
  if(typeof window === 'undefined') return htmlString;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  let currentLength = 0;
  let truncatedContent = '';
  const walkDOM = (node) => {
    if (currentLength >= maxLength) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const remainingLength = maxLength - currentLength;
      if (currentLength + text.length <= maxLength) {
        truncatedContent += text;
        currentLength += text.length;
      } else {
        truncatedContent += text.substring(0, remainingLength);
        currentLength = maxLength;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      truncatedContent += `<${tagName}`;
      for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        truncatedContent += ` ${attr.name}=\"${attr.value}\"`;
      }
      truncatedContent += '>';
      for (let i = 0; i < node.childNodes.length; i++) {
        if (currentLength >= maxLength) break;
        walkDOM(node.childNodes[i]);
      }
      truncatedContent += `</${tagName}>`;
    }
  };
  walkDOM(tempDiv);
  return truncatedContent;
};

const InstructionsToUse = ({ content }) => {
  const { text: instructionsText, video } = typeof content === 'object' ? content : { text: content, video: null };
  const text = sanitizeHtml(instructionsText);
  const safeText = typeof text === 'string' ? text : '';

  const [showFull, setShowFull] = useState(false);

  // Get the visible text (no HTML tags)
  const visibleText = useMemo(() => getVisibleText(safeText), [safeText]);

  // Truncate for display
  const truncatedText = useMemo(() => {
    if (isHTMLString(safeText)) {
      return truncateHTML(safeText, 1000);
    } else {
      return safeText.length > 1000 ? safeText.substring(0, 1000) + '...' : safeText;
    }
  }, [safeText]);

  // Only show the button if the visible text is longer than 1000 characters
  // and the truncated version is actually different from the full version
  const shouldTruncate = useMemo(() => {
    if (!safeText) return false;
    if (visibleText.length <= 1000) return false;
    if (isHTMLString(safeText)) {
      return getVisibleText(truncatedText) !== visibleText;
    } else {
      return safeText.length > 1000;
    }
  }, [safeText, visibleText, truncatedText]);

  return (
    <section className={css.panel} aria-labelledby="instructions-heading">
      <h3 id="instructions-heading" className={css.heading}>
        Instructions to Use
      </h3>

     <article className={css.content}>
        {safeText ? (
          isHTMLString(safeText) ? (
            <div dangerouslySetInnerHTML={{ __html: showFull ? safeText : truncatedText }} />
          ) : (
            <pre>{showFull ? safeText : truncatedText}</pre>
          )
        ) : (
          <p>No instructions provided.</p>
        )}
        {shouldTruncate && (
          <button
            className={css.readMoreButton}
            onClick={() => setShowFull((v) => !v)}
            type="button"
          >
            {showFull ? 'Read Less' : 'Read More'}
          </button>
        )}
      </article>

      {video?.playback_url && (
        <figure className={css.video}>
          <ReactPlayer
            url={video.playback_url}
            controls
            width="100%"
            height={414}
            style={{ borderRadius: 8, background: '#000' }}
          />
          <figcaption style={{ display: 'none' }}>Instructional video</figcaption>
        </figure>
      )}
    </section>
  );
};

InstructionsToUse.propTypes = {
  content: PropTypes.string.isRequired,
};

export default InstructionsToUse; 